import React, { useState, useEffect, useRef } from "react";
import * as StyledDOM from "./MultipleSelect.jsx";

export default function DynamicMultipleSelectCheckmarks({
  options = [],
  label = "Select",
  width = "",
  labelKey = "title",
  valueKey = labelKey,
  placeholder = "",
  value = [],
  onChange,
  disabled = false,
  searchable = true,
  onSearchChange,
  loading = false,
  resetSearchTerm = false,
  arrowIcon,
}) {
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const safeOptions = Array.isArray(options) ? options.filter(Boolean) : [];
  const selectedValues = Array.isArray(value) ? value : [];

  const getOptionValue = (option) => option?.[valueKey];
  const getOptionLabel = (option) =>
    (option?.[labelKey] ?? option?.label ?? option?.title ?? "N/A").toString();

  const selectableValues = safeOptions
    .map(getOptionValue)
    .filter((optionValue) => optionValue !== undefined && optionValue !== null);

  const selectedCount = selectedValues.length;

  const searchLabel = (placeholder || label || "options")
    .toString()
    .replace(/^select\s+/i, "")
    .replace(/\(s\)/i, "s")
    .toLowerCase();
  const searchPlaceholder = `Search ${searchLabel}${
    /s$/.test(searchLabel) ? "" : "s"
  }...`;

  const getInitials = (text) => {
    const words = text.match(/[\p{L}\p{N}][\p{L}\p{M}\p{N}]*/gu) || [];
    if (!words.length) return "?";
    return words
      .slice(0, 2)
      .map((word) => word[0])
      .join("")
      .toUpperCase();
  };

  const getAvatarColor = (text) => {
    const colors = [
      "#8b7cf6",
      "#3ba7f0",
      "#24b36b",
      "#e58a1f",
      "#ee5a7b",
      "#a66bea",
      "#28bfb3",
    ];
    const colorIndex = text
      .split("")
      .reduce((sum, char) => sum + char.charCodeAt(0), 0);

    return colors[colorIndex % colors.length];
  };

  const handleChange = (optionValue) => {
    if (disabled) return;
    if (optionValue != null) {
      if (selectedValues.includes(optionValue)) {
        onChange(selectedValues.filter((v) => v !== optionValue));
      } else {
        onChange([...selectedValues, optionValue]);
      }
    }
  };

  const handleCheckAll = () => {
    if (disabled) return;
    const combined = Array.from(
      new Set([...selectedValues, ...selectableValues]),
    );
    onChange(combined);
  };
  const handleUncheckAll = () => {
    if (disabled) return;
    if (onSearchChange && searchTerm) {
      onChange(selectedValues.filter((v) => !selectableValues.includes(v)));
    } else {
      onChange([]);
    }
  };

  const handleClearSelection = (event) => {
    event.stopPropagation();
    if (disabled) return;
    onChange([]);
  };

  const handleSearchChange = (event) => {
    const nextSearchTerm = event.target.value;
    setSearchTerm(nextSearchTerm);
    if (typeof onSearchChange === "function") {
      onSearchChange(nextSearchTerm);
    }
  };

  const toggleDropdown = () => {
    if (disabled) {
      setIsOpen(false);
      return;
    }
    const nextOpen = !isOpen;
    setIsOpen(nextOpen);
    if (!nextOpen && onSearchChange && searchTerm) {
      setSearchTerm("");
      onSearchChange("");
    }
    if(resetSearchTerm) setSearchTerm("");
  };

  const handleDone = () => {
    setIsOpen(false);
    if (onSearchChange && searchTerm) {
      setSearchTerm("");
      onSearchChange("");
    }
    if(resetSearchTerm) setSearchTerm("");
  };

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsOpen(false);
      if (onSearchChange && searchTerm) {
        setSearchTerm("");
        onSearchChange("");
      }
      if(resetSearchTerm) setSearchTerm("");
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (disabled && isOpen) {
      setIsOpen(false);
    }
  }, [disabled, isOpen]);

  useEffect(() => {
    if (isOpen && searchable) {
      searchInputRef.current?.focus();
    }
  }, [isOpen, searchable]);

  const filteredOptions = searchable
    ? onSearchChange
      ? safeOptions
      : safeOptions.filter((option) => {
          const labelText = getOptionLabel(option).toLowerCase();
          const searchLower = (searchTerm ?? "").toString().toLowerCase();
          return labelText.includes(searchLower);
        })
    : safeOptions;

  return (
    <StyledDOM.StyledDropdownContainer
      className="dropdown-container"
      style={{
        width,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.65 : 1,
      }}
      ref={dropdownRef}
    >
      <StyledDOM.StyledTextfieldContainer
        className="dropdown-textfield"
        label={label}
        placeholder={placeholder}
        onClick={toggleDropdown}
        value={selectedCount > 0 ? `${selectedCount} Selected` : ""}
        disabled={disabled}
        readOnly
        inputStyle={disabled ? { cursor: "not-allowed" } : undefined}
      />
      {selectedCount > 0 && (
        <StyledDOM.StyledClearButton
          type="button"
          aria-label="Clear selected options"
          onClick={handleClearSelection}
        >
          <StyledDOM.StyledClearIcon />
        </StyledDOM.StyledClearButton>
      )}
      <StyledDOM.StyledDropdownArrow
        as={arrowIcon}
        isOpen={isOpen}
        hasLabel={Boolean(label)}
      />

      {isOpen && (
        <StyledDOM.StyledDropdownMenu className="dropdown-menu">
          <StyledDOM.StyledStickyContainer className="sticky-container">
            {searchable && (
            <StyledDOM.StyledDropdownSearchItem className="search-button-dropdown-item">
              <StyledDOM.StyledSearchWrapper>
                <StyledDOM.StyledSearchIcon />
                <StyledDOM.StyledSearchContainer
                  ref={searchInputRef}
                  type="text"
                  className="search-input"
                  placeholder={searchPlaceholder}
                  value={searchTerm}
                  onChange={handleSearchChange}
                  onClick={(event) => event.stopPropagation()}
                />
              </StyledDOM.StyledSearchWrapper>
            </StyledDOM.StyledDropdownSearchItem>
          )}
            <StyledDOM.StyledCheckContainer className="multiselect-check-container">
              <StyledDOM.StyledActionButton
                type="button"
                onClick={handleCheckAll}
              >
                Select all
              </StyledDOM.StyledActionButton>

              <StyledDOM.StyledSelectedCount>
                {selectedCount > selectableValues.length
                  ? `${selectedCount} selected`
                  : `${selectedCount} of ${selectableValues.length} selected`}
              </StyledDOM.StyledSelectedCount>

              <StyledDOM.StyledActionButton
                type="button"
                onClick={handleUncheckAll}
              >
                Clear all
              </StyledDOM.StyledActionButton>
            </StyledDOM.StyledCheckContainer>
          </StyledDOM.StyledStickyContainer>
          <StyledDOM.StyledOptionsList>
            {loading ? (
              <StyledDOM.StyledEmptyState>
                Loading...
              </StyledDOM.StyledEmptyState>
            ) : filteredOptions.length ? (
              filteredOptions.map((option) => {
                const optionValue = getOptionValue(option);
                const optionLabel = getOptionLabel(option);
                const isSelected = selectedValues.includes(optionValue);

                return (
                  <StyledDOM.StyledDropdownItem
                    key={optionValue ?? optionLabel}
                    className="dropdown-item"
                    onClick={() => handleChange(optionValue)}
                    selected={isSelected}
                    role="option"
                    aria-selected={isSelected}
                  >
                    <StyledDOM.StyledOptionCheckbox selected={isSelected} />
                    <StyledDOM.StyledOptionAvatar
                      color={getAvatarColor(optionLabel)}
                    >
                      {getInitials(optionLabel)}
                    </StyledDOM.StyledOptionAvatar>
                    <StyledDOM.StyledOptionLabel title={optionLabel}>
                      {optionLabel}
                    </StyledDOM.StyledOptionLabel>
                  </StyledDOM.StyledDropdownItem>
                );
              })
            ) : (
              <StyledDOM.StyledEmptyState>
                No options found
              </StyledDOM.StyledEmptyState>
            )}
          </StyledDOM.StyledOptionsList>
          <StyledDOM.StyledFooter>
            <StyledDOM.StyledDoneButton type="button" onClick={handleDone}>
              Done
            </StyledDOM.StyledDoneButton>
          </StyledDOM.StyledFooter>
        </StyledDOM.StyledDropdownMenu>
      )}
    </StyledDOM.StyledDropdownContainer>
  );
}
